import { concat } from '@ember/helper';
import { getOwner } from '@ember/owner';
import type TelemetryService from '../../services/telemetry.ts';
import { getSingleValueForTasField } from '../../utils/tuition-assistance/fields.ts';
import { limitsEvaluationDate } from '../../utils/tuition-assistance/limits-evaluation-date.ts';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Container from './limits-summary/container.gts';
import { divide } from 'tio-ui/math';
import ErrorAlert from './limits-summary/error-alert.gts';
import LineItem from './limits-summary/line-item.gts';
import safeFormatNumber from '../../helpers/safe-format-number.ts';
import { formatUsd } from '../../utils/usd-format.ts';
import type { LimitsStatusDataSignature } from '../../services/tuition-assistance.ts';
import type { WithBoundArgs } from '@glint/template';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type IntlService from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
import type { TasApplicationExtension as TasApplication } from 'tio-data/data/resources/tas-application.ext';
import type TuitionAssistanceService from '../../services/tuition-assistance.ts';

const number = Number;

export interface TuitionAssistanceLimitsSummarySignature {
  Args: {
    application: TasApplication;
    employee: EmployeeModel;
    /** Cents the employee submission will stamp; omitted callers use the raw request. */
    requestedAmountToValidate?: number;
    approvedTotalToValidate?: number;
    approvedCourseCashTotalToValidate?: number;
    labelClass?: string;
    valueClass?: string;
    ignoreLimitsValidation?: boolean;
    paymentMethod?: string;
    shouldFetchLimits?: boolean;
  };
  Blocks: {
    default: [
      {
        Container: WithBoundArgs<typeof Container, 'title' | 'titleClass'>;
        LineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass' | 'sign'
        >;
        MaxAnnualBenefitLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        BenefitPaidForYearLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        RemainingBenefitLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        ApplicationAmountRequestedLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        ApplicationAmountBasedOnGPALineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        LimitsAlertForEmployeeIfApplicable: WithBoundArgs<
          typeof ErrorAlert,
          'showError' | 'errorMessage' | 'explanation'
        >;
        LimitsAlertForApproverIfApplicable: WithBoundArgs<
          typeof ErrorAlert,
          'showError' | 'errorMessage' | 'explanation'
        >;
        ApplicationAmountRequestedWithoutScholarshipsLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass' | 'sign'
        >;
        ScholarshipAmountLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass' | 'sign'
        >;
        ApplicationCourseCashSubtotalLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        ApplicationAllOtherExpensesSubtotalLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        hasLimitsErrorForEmployee: boolean;
        hasLimitsErrorForApprover: boolean;
        isDisabledSubmitForEmployee: boolean;
        isDisabledSubmitForApprover: boolean;
        isUnlimitedMaximumAnnualBenefit: boolean;
        isUnlimitedRemainingBenefit: boolean;
        isCredits: boolean;
        refreshData: () => void;
      },
    ];
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceLimitsSummaryComponent extends Component<TuitionAssistanceLimitsSummarySignature> {
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare intl: IntlService;

  @tracked private _limitStatus: LimitsStatusDataSignature | undefined;
  // Set when the most recent limits fetch failed. `_limitStatus` deliberately keeps its
  // previous value so a later successful refresh can diff, but no getter may validate
  // against it: a stale balance is exactly wrong right after the action that consumed it.
  @tracked private limitStatusLoadFailed = false;
  @tracked isLoading = false;
  getSingleValueForTasField = getSingleValueForTasField;

  constructor(owner: Owner, args: TuitionAssistanceLimitsSummarySignature['Args']) {
    super(owner, args);
    this.tuitionAssistance.registerLimitsSummaryComponent(this);
    this.initializeData();

    registerDestructor(this, () => this.tuitionAssistance.unregisterLimitsSummaryComponent(this));
  }

  get shouldFetchLimits(): boolean {
    return this.args.shouldFetchLimits ?? true;
  }

  async initializeData() {
    this.isLoading = true;
    if (this.args.application && this.args.employee) {
      await this.loadLimitStatusTask();
    }
    this.isLoading = false;
  }

  loadLimitStatusTask = async () => {
    const { employee, application } = this.args;
    if (!employee || !application) return;

    try {
      this._limitStatus = await this.tuitionAssistance.getV2LimitsStatus(
        employee,
        application,
        limitsEvaluationDate(application, { paymentMethod: this.paymentMethod })
      );
      this.limitStatusLoadFailed = false;
    } catch (error) {
      // Not only the initial load: refreshData()/refreshActiveLimitsSummaryComponents()
      // re-enter here after approve/edit actions, and a failure there must surface as
      // "unavailable" rather than silently validating the pre-action balance.
      this.limitStatusLoadFailed = true;
      console.error('Error loading V2 limits status:', error);
    }
  };

  get limitStatus(): LimitsStatusDataSignature | undefined {
    return this.limitStatusLoadFailed ? undefined : this._limitStatus;
  }

  get isCredits() {
    return this.limitStatus?.limit_capacity === 'TAS.LimitCapacity.CREDITS';
  }

  get maximumAnnualBenefit() {
    const details = this.limitStatus?.details;
    if (!details) return null;
    // Use the most specific non-null annual limit (scheme = program limit)
    return (
      details.scheme?.annual_limit ??
      details.batch?.annual_limit ??
      details.product?.annual_limit ??
      details.company?.annual_limit ??
      null
    );
  }

  get isUnlimitedMaximumAnnualBenefit() {
    if (!this.limitStatus) {
      return false;
    }
    return this.maximumAnnualBenefit === null;
  }

  get remainingBenefit() {
    return this.limitStatus?.available ?? null;
  }

  get isUnlimitedRemainingBenefit() {
    if (!this.limitStatus) {
      return false;
    }
    return this.remainingBenefit === null;
  }

  /**
   * The limit check ran to completion but produced nothing to check against —
   * /v2/limits/status failed (403/500/401), the network dropped, or the request was
   * never issued because `employee` was missing.
   *
   * This is NOT the same as "$0 remaining", and not the same as unlimited: a limit
   * row with `available: null` means unlimited and is handled above. Here we know
   * nothing, so we fail closed and say so, rather than asserting the employee
   * exceeded a limit we never read.
   */
  get isLimitStatusUnavailable() {
    return this.shouldFetchLimits && !this.isLoading && !this.limitStatus;
  }

  get totalBenefitPaidForYear() {
    const details = this.limitStatus?.details;
    if (!details) return 0;
    // Use the same tier-priority as maximumAnnualBenefit to ensure usage units match the limit units
    return (
      details.scheme?.annual_usage ??
      details.batch?.annual_usage ??
      details.product?.annual_usage ??
      details.company?.annual_usage ??
      0
    );
  }

  get originalAmountRequested() {
    if (this.isCredits) {
      return this.args.application?.totalCredits || 0;
    }
    return this.args.application?.requestedTotal || 0;
  }

  get currentApprovedTotal() {
    if (this.isCredits) {
      return this.args.application?.totalCredits || 0;
    }
    const base =
      this.args.approvedTotalToValidate ??
      this.args.application.approvedTotal ??
      this.originalAmountRequested;
    const courseCashApproved = this.isCourseCashPaymentMethod
      ? (this.args.approvedCourseCashTotalToValidate ?? 0)
      : 0;

    return base + courseCashApproved;
  }

  get employeeAmountRequested() {
    return this.isCredits
      ? this.originalAmountRequested
      : (this.args.requestedAmountToValidate ?? this.originalAmountRequested);
  }

  get isValidOriginalAmountRequested() {
    if (this.isUnlimitedRemainingBenefit) {
      return true;
    }
    return (this.remainingBenefit || 0) - this.employeeAmountRequested >= 0;
  }

  get isValidCurrentApprovedTotal() {
    if (this.isUnlimitedRemainingBenefit) {
      return true;
    }
    return (this.remainingBenefit || 0) - this.currentApprovedTotal >= 0;
  }

  /**
   * The template is UNREACHABLE here, never absent — the distinction is the whole point of
   * this report. An application cannot exist without a program instance, and an instance
   * cannot exist without a template: both columns are NOT NULL behind real FKs, with no
   * soft-delete and no default scope. On top of that, ~20 getters in tas-application.ext.ts
   * traverse this same chain to render the page at all, so a genuinely absent template would
   * make the page uniformly wrong rather than break one getter.
   *
   * So a null here can only be client-side relationship state that lost the link, and it is
   * not yet explained: see Datadog issue da24e4ac, where it crashed the approve modal on an
   * over-limit application and then did not reproduce 36s later on the same record. Do not
   * go looking for a program without a template — the database cannot represent one.
   *
   * Guarding the read alone would bury the next occurrence, so report it once per component
   * with the ids and load state needed to trace it to a request.
   */
  #reportedUnreachableProgramTemplate = false;

  // Not tracked: this is a report-once latch, so mutating it during render cannot
  // invalidate anything or re-enter the getter.
  #reportUnreachableProgramTemplate() {
    if (this.#reportedUnreachableProgramTemplate) {
      return;
    }
    this.#reportedUnreachableProgramTemplate = true;

    const error = new Error(
      'TAS limits summary: program template unreachable through the program instance — ' +
        'client-side relationship state, not missing data (both FKs are NOT NULL)'
    );
    const context = {
      applicationId: this.args.application?.id,
      programInstanceId: this.args.application?.tasProgramInstance?.id,
      // Which link broke: the instance itself, or only the instance's template.
      hasProgramInstance: Boolean(this.args.application?.tasProgramInstance),
      hasLimitStatus: Boolean(this.limitStatus),
      isLoadingLimitStatus: this.isLoading,
    };

    // Owner lookup rather than @service because not every consuming app registers
    // service:telemetry (test-app does not); where absent this quietly no-ops.
    const telemetry = getOwner(this)?.lookup('service:telemetry') as TelemetryService | undefined;
    telemetry?.trackError(error, context);
  }

  get isAllowedSubmissionBeyondLimits() {
    if (this.args.application.ignorePolicies) {
      return true;
    }
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    if (!template) {
      this.#reportUnreachableProgramTemplate();
    }
    return (
      getSingleValueForTasField('ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT', template?.fields) ||
      false
    );
  }

  get hasLimitsErrorForEmployee() {
    if (
      this.isAllowedSubmissionBeyondLimits ||
      this.isLoading ||
      this.args.application?.isDormantState ||
      !this.shouldFetchLimits
    ) {
      return false;
    }
    return !this.isValidOriginalAmountRequested;
  }

  get hasLimitsErrorForApprover() {
    if (
      this.isLoading ||
      this.args.application?.isDormantState ||
      this.args.ignoreLimitsValidation
    ) {
      return false;
    }
    return !this.isValidCurrentApprovedTotal;
  }

  get isDisabledSubmitForEmployee() {
    if (this.isAllowedSubmissionBeyondLimits) {
      return false;
    }
    return this.hasLimitsErrorForEmployee;
  }

  get isDisabledSubmitForApprover() {
    if (this.isAllowedSubmissionBeyondLimits) {
      return false;
    }
    return this.hasLimitsErrorForApprover;
  }

  get reductionNeededToOriginalAmountRequested() {
    if (this.isValidOriginalAmountRequested) {
      return 0;
    }
    const reductionBasis = Math.max(this.originalAmountRequested, this.employeeAmountRequested);
    return reductionBasis - (this.remainingBenefit || 0);
  }

  get reductionNeededToCurrentApprovedTotal() {
    if (this.isValidCurrentApprovedTotal) {
      return 0;
    }
    return Math.abs((this.remainingBenefit || 0) - this.currentApprovedTotal);
  }

  get formattedReductionNeededIfApplicable() {
    if (this.isCredits) {
      return this.intl.t('tuition_assistance.limits_summary.messages.credits', {
        count: this.reductionNeededToOriginalAmountRequested,
      });
    }
    return formatUsd(this.reductionNeededToOriginalAmountRequested / 100, this.intl.primaryLocale, {
      maximumFractionDigits: 2,
    });
  }

  get formattedReductionNeededForApprover() {
    if (this.isCredits) {
      return this.intl.t('tuition_assistance.limits_summary.messages.credits', {
        count: this.reductionNeededToCurrentApprovedTotal,
      });
    }
    return formatUsd(this.reductionNeededToCurrentApprovedTotal / 100, this.intl.primaryLocale, {
      maximumFractionDigits: 2,
    });
  }

  get noRemainingBenefitsMessageForEmployee() {
    return this.intl.t('tuition_assistance.limits_summary.messages.no_benefits_employee');
  }

  get noRemainingBenefitsMessageForApprover() {
    return this.intl.t('tuition_assistance.limits_summary.messages.no_benefits_approver');
  }

  get unavailableLimitStatusMessageForEmployee() {
    return this.intl.t('tuition_assistance.limits_summary.messages.unavailable_employee');
  }

  get unavailableLimitStatusMessageForApprover() {
    return this.intl.t('tuition_assistance.limits_summary.messages.unavailable_approver');
  }

  get limitsErrorMessageForEmployee() {
    if (this.isLimitStatusUnavailable) {
      return this.unavailableLimitStatusMessageForEmployee;
    }
    return (this.remainingBenefit || 0) <= 0
      ? this.noRemainingBenefitsMessageForEmployee
      : this.intl.t('tuition_assistance.limits_summary.messages.employee_exceeded_max', {
          amount: this.formattedReductionNeededIfApplicable,
        });
  }

  get limitsErrorMessageForApprover() {
    // Beyond-limit programs outrank the unavailable state: under that flag not even a
    // CONFIRMED over-limit blocks approval, so an UNVERIFIED balance cannot either —
    // the message must agree with the enabled button.
    if (this.isAllowedSubmissionBeyondLimits) {
      return this.intl.t('tuition_assistance.limits_summary.messages.beyond_limits_allowed');
    }
    if (this.isLimitStatusUnavailable) {
      return this.unavailableLimitStatusMessageForApprover;
    }
    if (this.isCredits) {
      return this.intl.t('tuition_assistance.limits_summary.messages.over_credit_limit');
    }
    return this.intl.t('tuition_assistance.limits_summary.messages.over_amount_limit');
  }

  get activeLimitName() {
    const rawLimitName = this.limitStatus?.soft_limit || '';
    switch (rawLimitName) {
      case 'company_annual':
        return this.intl.t('tuition_assistance.limits_summary.messages.limit_names.company_annual');
      case 'company':
        return this.intl.t('tuition_assistance.limits_summary.messages.limit_names.company');
      case 'product_annual':
        return this.intl.t('tuition_assistance.limits_summary.messages.limit_names.product_annual');
      case 'product':
        return this.intl.t('tuition_assistance.limits_summary.messages.limit_names.product');
      case 'batch_annual':
        return this.intl.t('tuition_assistance.limits_summary.messages.limit_names.batch_annual');
      case 'batch':
        return this.intl.t('tuition_assistance.limits_summary.messages.limit_names.batch');
      case 'scheme_annual':
        return this.intl.t('tuition_assistance.limits_summary.messages.limit_names.scheme_annual');
      case 'scheme':
        return this.intl.t('tuition_assistance.limits_summary.messages.limit_names.scheme');
      case 'none':
        return '';
      default:
        return rawLimitName;
    }
  }

  /**
   * Shown in the "explain limit" dialog when there is no limit status. Says plainly that
   * this is a system problem rather than a limit problem, so nobody wastes time reducing
   * an amount that was never compared against anything.
   */
  get unavailableLimitStatusExplanation() {
    return this.intl.t('tuition_assistance.limits_summary.messages.unavailable_explanation');
  }

  get limitsErrorExplanationForEmployee() {
    if (this.isLimitStatusUnavailable) {
      return this.unavailableLimitStatusExplanation;
    }
    const additionalText =
      // @ts-expect-error: @Julia I think null means something here, but I'm not sure what
      this.remainingBenefit <= 0
        ? this.noRemainingBenefitsMessageForEmployee
        : this.intl.t('tuition_assistance.limits_summary.messages.employee_over_by', {
            amount: this.formattedReductionNeededIfApplicable,
          });
    return this.intl.t('tuition_assistance.limits_summary.messages.employee_exceeded_plan', {
      limitName: this.activeLimitName,
      details: additionalText,
    });
  }

  get limitsErrorExplanationForApprover() {
    // Keep the dialog coherent with the message above: beyond-limit first, then
    // unavailable, then the numeric explanations that need a real balance.
    if (this.isAllowedSubmissionBeyondLimits) {
      return this.intl.t('tuition_assistance.limits_summary.messages.beyond_limits_allowed');
    }
    if (this.isLimitStatusUnavailable) {
      return this.unavailableLimitStatusExplanation;
    }
    // @ts-expect-error: @Julia I think null means something here, but I'm not sure what
    if (this.remainingBenefit <= 0) {
      return this.intl.t(
        'tuition_assistance.limits_summary.messages.approver_exceeded_no_benefits',
        {
          limitName: this.activeLimitName,
          details: this.noRemainingBenefitsMessageForApprover,
        }
      );
    }

    const formatValue = (amount: number) => {
      if (this.isCredits) {
        return this.intl.t('tuition_assistance.limits_summary.messages.credits', { count: amount });
      }
      return formatUsd(amount / 100, this.intl.primaryLocale, { maximumFractionDigits: 2 });
    };

    const formattedMaxBenefit = formatValue(this.maximumAnnualBenefit || 0);
    const formattedPaidThisYear = formatValue(this.totalBenefitPaidForYear);
    const formattedRemaining = formatValue(this.remainingBenefit || 0);
    const formattedRequestedAmount = formatValue(this.currentApprovedTotal);

    return this.intl.t('tuition_assistance.limits_summary.messages.approver_over_limit_details', {
      limitName: this.activeLimitName,
      maxBenefit: formattedMaxBenefit,
      paidThisYear: formattedPaidThisYear,
      remaining: formattedRemaining,
      requested: formattedRequestedAmount,
      overBy: this.formattedReductionNeededForApprover,
    });
  }

  get totalBasedOnGPA() {
    if (this.isCredits) {
      return this.args.application.totalCredits;
    }
    return Math.min(this.tuitionAssistance.gpaBasedAmount, this.args.application.requestedTotal);
  }

  get paymentMethod() {
    if (this.args.paymentMethod) {
      return this.args.paymentMethod;
    }
    return this.args.application.paymentMethod || 'TAS.AssistanceManner.REIMBURSEMENT';
  }

  get isCourseCashPaymentMethod() {
    return this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get courseCashSubtotalBasedOnPaymentMethod() {
    const courseCashSubtotal = this.args.application.requestedCourseCashSubtotal;
    if (this.isCourseCashPaymentMethod) {
      return courseCashSubtotal;
    }
    return 0;
  }

  get nonCourseCashSubtotalBasedOnPaymentMethod() {
    const nonCourseCashSubtotal = this.args.application.requestedNonCourseCashSubtotal;
    if (this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
      return nonCourseCashSubtotal;
    }
    return this.args.application.requestedTotalAllCourses;
  }

  // TODO: There is an edge case now where an approver can edit the approved total
  // and it would alter the assistances' amounts.
  // We need to use the exposed retry function to refresh the data when the approver saves the edit.
  refreshData = async () => {
    this.isLoading = true;
    // Same condition as the initial load: shouldFetchLimits gates the employee error state, not the display.
    if (this.args.application && this.args.employee) {
      await this.loadLimitStatusTask();
    }
    this.isLoading = false;
  };

  <template>
    {{yield
      (hash
        Container=(component Container)
        LineItem=(component LineItem labelClass=@labelClass valueClass=@valueClass)
        ScholarshipAmountLineItem=(component
          LineItem
          label=(t "tuition_assistance.limits_summary.scholarships")
          value=(if
            this.isCredits
            (concat (safeFormatNumber @application.scholarshipsTotal style="decimal") " credits")
            (safeFormatNumber
              (divide @application.scholarshipsTotal 100) style="currency" currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
          sign="plus"
        )
        ApplicationAmountRequestedLineItem=(component
          LineItem
          label=(t "tuition_assistance.limits_summary.total_request")
          value=(if
            this.isCredits
            (concat (safeFormatNumber @application.totalCredits style="decimal") " credits")
            (safeFormatNumber
              (divide @application.requestedTotal 100) style="currency" currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        ApplicationAmountBasedOnGPALineItem=(component
          LineItem
          label=(t "tuition_assistance.limits_summary.total_request")
          value=(if
            this.isCredits
            (concat (safeFormatNumber @application.totalCredits style="decimal") " credits")
            (safeFormatNumber (divide this.totalBasedOnGPA 100) style="currency" currency="USD")
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        ApplicationAmountRequestedWithoutScholarshipsLineItem=(component
          LineItem
          label=(t "tuition_assistance.limits_summary.requested_amount")
          value=(if
            this.isCredits
            (concat (safeFormatNumber @application.totalCredits style="decimal") " credits")
            (safeFormatNumber
              (divide @application.requestedTotalAllCourses 100) style="currency" currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        MaxAnnualBenefitLineItem=(component
          LineItem
          label=(if
            this.isCredits
            (t "tuition_assistance.limits_summary.eligible_credit_limit")
            (t "tuition_assistance.limits_summary.program_limit")
          )
          value=(if
            this.isCredits
            (if
              this.isUnlimitedRemainingBenefit
              ""
              (concat (safeFormatNumber (number this.remainingBenefit) style="decimal") " credits")
            )
            (if
              this.isUnlimitedMaximumAnnualBenefit
              ""
              (safeFormatNumber
                (divide (number this.maximumAnnualBenefit) 100) style="currency" currency="USD"
              )
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        BenefitPaidForYearLineItem=(component
          LineItem
          label=(if
            this.isCredits
            (t "tuition_assistance.limits_summary.pending_credit_limit")
            (t "tuition_assistance.limits_summary.benefit_paid_and_approved")
          )
          value=(if
            this.isCredits
            (concat (safeFormatNumber this.totalBenefitPaidForYear style="decimal") " credits")
            (safeFormatNumber
              (divide this.totalBenefitPaidForYear 100) style="currency" currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        RemainingBenefitLineItem=(component
          LineItem
          label=(if
            this.isCredits
            (t "tuition_assistance.limits_summary.remaining_credit_limit")
            (t "tuition_assistance.limits_summary.benefit_remaining")
          )
          value=(if
            this.isUnlimitedRemainingBenefit
            ""
            (if
              this.isCredits
              (concat (safeFormatNumber (number this.remainingBenefit) style="decimal") " credits")
              (safeFormatNumber
                (divide (number this.remainingBenefit) 100) style="currency" currency="USD"
              )
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        LimitsAlertForEmployeeIfApplicable=(component
          ErrorAlert
          showError=this.hasLimitsErrorForEmployee
          errorMessage=this.limitsErrorMessageForEmployee
          explanation=this.limitsErrorExplanationForEmployee
        )
        LimitsAlertForApproverIfApplicable=(component
          ErrorAlert
          showError=this.hasLimitsErrorForApprover
          errorMessage=this.limitsErrorMessageForApprover
          explanation=this.limitsErrorExplanationForApprover
        )
        ApplicationAllOtherExpensesSubtotalLineItem=(component
          LineItem
          value=(if
            this.isCredits
            (safeFormatNumber this.nonCourseCashSubtotalBasedOnPaymentMethod style="decimal")
            (safeFormatNumber
              (divide this.nonCourseCashSubtotalBasedOnPaymentMethod 100)
              style="currency"
              currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        ApplicationCourseCashSubtotalLineItem=(component
          LineItem
          value=(if
            this.isCredits
            (safeFormatNumber this.courseCashSubtotalBasedOnPaymentMethod style="decimal")
            (safeFormatNumber
              (divide this.courseCashSubtotalBasedOnPaymentMethod 100)
              style="currency"
              currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=(concat @labelClass " " "flex")
          valueClass=@valueClass
        )
        hasLimitsErrorForApprover=this.hasLimitsErrorForApprover
        hasLimitsErrorForEmployee=this.hasLimitsErrorForEmployee
        isDisabledSubmitForEmployee=this.isDisabledSubmitForEmployee
        isDisabledSubmitForApprover=this.isDisabledSubmitForApprover
        isUnlimitedMaximumAnnualBenefit=this.isUnlimitedMaximumAnnualBenefit
        isUnlimitedRemainingBenefit=this.isUnlimitedRemainingBenefit
        isCredits=this.isCredits
        refreshData=this.refreshData
      )
    }}
  </template>
}
